import { useState, useMemo } from 'react';
import { PlusCircle, XCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MovimientosTable } from './shared/MovimientosTable';
import { RegistrarMovimientoModal } from './shared/RegistrarMovimientoModal';
import { FormField } from './shared/FormField';
import { NativeSelect } from './shared/NativeSelect';
import { DateInput } from './shared/DateInput';
import { SearchInput } from './shared/SearchInput';
import { useMovimientos } from '../hooks/useMovimientos';
import { useCajas } from '../hooks/useCajas';
import { useAlert } from '@/hooks/useAlert';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { CARD_BASE } from '@/contabilidad/constants/ui';
import { cn } from '@/lib/utils';
import type { CreateMovimientoPayload, Movimiento } from '../types';

function parseDate(ddmmyyyy: string): Date | null {
  const [d, m, y] = ddmmyyyy.split('/');
  if (!d || !m || !y) return null;
  return new Date(Number(y), Number(m) - 1, Number(d));
}

const DEFAULTS = {
  desde: '',
  hasta: '',
  tipo: 'todos',
  moneda: 'todas',
  categoria: 'todas',
  caja: 'todas',
  estado: 'todos',
  search: '',
};

export function MovimientosPage() {
  const { successAlert, errorAlert, questionAlert } = useAlert();
  const { movimientos, isLoading, createMovimiento, isCreating, updateMovimiento, isUpdating, deleteMovimiento } = useMovimientos();
  const { cajas } = useCajas();
  const { canCreateContabilidad, canEditContabilidad, canDeleteContabilidad } = useAuthorization();

  const [modalTipo, setModalTipo] = useState<'Ingreso' | 'Egreso' | null>(null);
  const [editing, setEditing] = useState<Movimiento | null>(null);

  const [desde, setDesde] = useState(DEFAULTS.desde);
  const [hasta, setHasta] = useState(DEFAULTS.hasta);
  const [tipo, setTipo] = useState(DEFAULTS.tipo);
  const [moneda, setMoneda] = useState(DEFAULTS.moneda);
  const [categoria, setCategoria] = useState(DEFAULTS.categoria);
  const [caja, setCaja] = useState(DEFAULTS.caja);
  const [estado, setEstado] = useState(DEFAULTS.estado);
  const [search, setSearch] = useState(DEFAULTS.search);

  const handleLimpiar = () => {
    setDesde(DEFAULTS.desde);
    setHasta(DEFAULTS.hasta);
    setTipo(DEFAULTS.tipo);
    setMoneda(DEFAULTS.moneda);
    setCategoria(DEFAULTS.categoria);
    setCaja(DEFAULTS.caja);
    setEstado(DEFAULTS.estado);
    setSearch(DEFAULTS.search);
  };

  // Derivados de los movimientos cargados
  const categorias = useMemo(
    () => [...new Set(movimientos.map((m) => m.categoria))].sort(),
    [movimientos],
  );
  const cajasFilter = useMemo(
    () => [...new Set(movimientos.map((m) => m.caja).filter(Boolean))].sort() as string[],
    [movimientos],
  );

  const filtered = useMemo(() => {
    const desdeDate = desde ? new Date(desde) : null;
    const hastaDate = hasta ? new Date(hasta) : null;

    return movimientos.filter((m) => {
      if (tipo !== 'todos' && m.tipo !== tipo) return false;
      if (moneda !== 'todas' && m.moneda !== moneda) return false;
      if (categoria !== 'todas' && m.categoria !== categoria) return false;
      if (caja !== 'todas' && m.caja !== caja) return false;
      if (estado !== 'todos' && m.estado !== estado) return false;
      if (desdeDate || hastaDate) {
        const fechaMov = parseDate(m.fecha);
        if (fechaMov) {
          if (desdeDate && fechaMov < desdeDate) return false;
          if (hastaDate && fechaMov > hastaDate) return false;
        }
      }
      if (search) {
        const q = search.toLowerCase();
        if (!m.descripcion.toLowerCase().includes(q) && !m.categoria.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [movimientos, desde, hasta, tipo, moneda, categoria, caja, estado, search]);

  async function handleSubmitMovimiento(payload: CreateMovimientoPayload) {
    try {
      if (editing) {
        await updateMovimiento({ id: editing.id, payload });
        successAlert({ title: 'Movimiento actualizado', message: 'Los cambios fueron guardados correctamente.' });
        setEditing(null);
      } else {
        await createMovimiento(payload);
        const label = payload.tipo === 'Ingreso' ? 'Ingreso' : 'Egreso';
        successAlert({ title: `${label} registrado`, message: 'El movimiento fue agregado correctamente.' });
        setModalTipo(null);
      }
    } catch (err) {
      errorAlert({ title: 'Error al guardar', exception: err });
    }
  }

  function handleEdit(m: Movimiento) {
    setEditing(m);
  }

  async function handleDelete(m: Movimiento) {
    const { isConfirmed } = await questionAlert({
      title: 'Eliminar movimiento',
      text: `¿Confirmás que querés eliminar el movimiento "${m.descripcion}" por ${m.moneda} $${Math.abs(m.monto).toLocaleString('es-AR')}? Esta acción no se puede deshacer.`,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!isConfirmed) return;
    try {
      await deleteMovimiento(m.id);
      successAlert({ title: 'Movimiento eliminado', message: 'El movimiento fue eliminado correctamente.' });
    } catch (err) {
      errorAlert({ title: 'Error al eliminar', exception: err });
    }
  }

  if (isLoading) {
    return <div className="p-5 text-sm text-muted-foreground">Cargando...</div>;
  }

  return (
    <>
      <div className={cn(CARD_BASE, 'space-y-5 p-5 min-h-full')}>

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs font-semibold text-primary uppercase tracking-widest">
            Movimientos
          </p>
          {canCreateContabilidad && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="rounded-full gap-1.5 text-blue-600 border-blue-400 bg-blue-50 hover:bg-blue-100 hover:text-blue-700"
                onClick={() => setModalTipo('Ingreso')}
              >
                <PlusCircle className="size-4" />
                Nuevo Ingreso
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="rounded-full gap-1.5 text-slate-600 border-slate-300 bg-slate-50 hover:bg-slate-100 hover:text-slate-700"
                onClick={() => setModalTipo('Egreso')}
              >
                <XCircle className="size-4" />
                Nuevo Egreso
              </Button>
            </div>
          )}
        </div>

        {/* Barra de filtros */}
        <div className="rounded-xl border border-gray-200 p-4 space-y-3">

          {/* Fila 1: Desde, Hasta, Tipo, Moneda, Categoría */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-3">
            <FormField label="Desde">
              <DateInput value={desde} onChange={(e) => setDesde(e.target.value)} />
            </FormField>
            <FormField label="Hasta">
              <DateInput value={hasta} onChange={(e) => setHasta(e.target.value)} />
            </FormField>
            <FormField label="Tipo">
              <NativeSelect value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="Ingreso">Ingreso</option>
                <option value="Egreso">Egreso</option>
              </NativeSelect>
            </FormField>
            <FormField label="Moneda">
              <NativeSelect value={moneda} onChange={(e) => setMoneda(e.target.value)}>
                <option value="todas">Todas</option>
                <option value="ARS">ARS</option>
                <option value="USD">USD</option>
              </NativeSelect>
            </FormField>
            <FormField label="Categoría">
              <NativeSelect value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                <option value="todas">Todas</option>
                {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
              </NativeSelect>
            </FormField>
          </div>

          {/* Fila 2: Caja, Estado, Buscar + Limpiar */}
          <div className="flex flex-wrap gap-3 items-end">
            <FormField label="Caja" className="w-full sm:w-52">
              <NativeSelect value={caja} onChange={(e) => setCaja(e.target.value)}>
                <option value="todas">Todas</option>
                {cajasFilter.map((c) => <option key={c} value={c}>{c}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Estado" className="w-full sm:w-44">
              <NativeSelect value={estado} onChange={(e) => setEstado(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="Completado">Completado</option>
                <option value="Pendiente">Pendiente</option>
                <option value="Parcial">Parcial</option>
              </NativeSelect>
            </FormField>
            <FormField label="Buscar" className="flex-1 min-w-48">
              <SearchInput
                placeholder="Buscar por descripción o cliente..."
                value={search}
                onChange={setSearch}
              />
            </FormField>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLimpiar}
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground shrink-0"
            >
              <X className="size-3.5" />
              Limpiar
            </Button>
          </div>
        </div>

        {/* Tabla */}
        <div className={cn(CARD_BASE, 'overflow-hidden')}>
          <MovimientosTable
            movimientos={filtered}
            showPagination
            onEdit={canEditContabilidad ? handleEdit : undefined}
            onDelete={canDeleteContabilidad ? handleDelete : undefined}
          />
        </div>

      </div>

      {(modalTipo || editing) && (
        <RegistrarMovimientoModal
          open={true}
          tipo={editing?.tipo ?? modalTipo!}
          cajas={cajas}
          movimiento={editing ?? undefined}
          isSubmitting={isCreating || isUpdating}
          onSubmit={handleSubmitMovimiento}
          onClose={() => { setModalTipo(null); setEditing(null); }}
        />
      )}
    </>
  );
}
